import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, type Package } from '../lib/api';
import { auth } from '../lib/auth';
import { isUserPremium } from '../lib/quota';
import { useScrollLock } from '../lib/scrollLock';
import { ROUTES } from '../routes/paths';
import {
  trackSubscriptionPrompt,
  trackViewPackages,
  trackSelectPackage,
} from '../lib/analytics';
import styles from './SubscriptionPromptModal.module.css';

interface SubscriptionPromptModalProps {
  visible: boolean;
  onClose: () => void;
}

export function CloseIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z" />
    </svg>
  );
}

export function SubscriptionPromptModal({ visible, onClose }: SubscriptionPromptModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  useScrollLock(visible);

  const {
    data: packagesData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['packages'],
    queryFn: api.packages,
    enabled: visible && !isUserPremium(),
  });

  React.useEffect(() => {
    if (visible && !isUserPremium()) {
      trackSubscriptionPrompt("quota_modal_prompt");
    }
  }, [visible]);

  React.useEffect(() => {
    if (visible && packagesData?.results && packagesData.results.length > 0) {
      trackViewPackages(packagesData.results);
    }
  }, [visible, packagesData]);

  if (!visible || isUserPremium()) return null;

  const handlePackageSelect = (item: Package) => {
    trackSelectPackage(item);
    onClose();
    const user = auth.user();
    if (!user || user.is_guest) {
      navigate(ROUTES.AUTH.LOGIN, { state: { from: ROUTES.SUBSCRIPTIONS.WITH_PACKAGE(item.id) } });
      return;
    }

    navigate(ROUTES.SUBSCRIPTIONS.WITH_PACKAGE(item.id));
  };

  return (
    <div className={styles.overlay} onClick={onClose} role="dialog" aria-modal="true">
      <div className={styles.bottomSheet} onClick={(e) => e.stopPropagation()}>
        <div className={styles.headerContainer}>
          <h2 className={styles.title}>
            {t('subscription.unlockPremiumExperience')}
          </h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label={t('common.close') || 'Close'}
          >
            <CloseIcon size={26} />
          </button>
        </div>

        <p className={styles.subtitle}>
          {t('subscription.unlimitedMusicMessage')}
        </p>

        <div className={styles.contentContainer}>
          {isLoading && (
            <div className={styles.spinnerContainer}>
              <div className={styles.spinner} />
            </div>
          )}

          {isError && (
            <p className={styles.errorMessage}>
              {t('subscription.couldNotLoadPackages')}
            </p>
          )}

          {packagesData && packagesData.results && packagesData.results.length > 0 && (
            <div className={styles.packagesList}>
              {packagesData.results.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={styles.packageButton}
                  onClick={() => handlePackageSelect(item)}
                >
                  {item.product_code === 'YRMUSIC_7999S' && (
                    <span className={styles.fireBadge} role="img" aria-label="Popular">
                      🔥
                    </span>
                  )}
                  <span className={styles.packageName}>
                    {item.name}
                  </span>
                  <span className={styles.packagePrice}>
                    {parseInt(String(item.price), 10)} Ks
                  </span>
                </button>
              ))}
            </div>
          )}

          {packagesData && (!packagesData.results || packagesData.results.length === 0) && !isLoading && !isError && (
            <p className={styles.emptyMessage}>
              No packages available at the moment.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default SubscriptionPromptModal;
